import { defineStore } from "pinia";
import * as cornerstoneTools from "@cornerstonejs/tools";
import { MouseBindings } from "@cornerstonejs/tools/enums/ToolBindings";
import * as cornerstone3D from "@cornerstonejs/core";
import {
  Enums as csToolsEnums,
  annotation,
  utilities,
} from "@cornerstonejs/tools";
import { metaData, utilities as csUtils } from "@cornerstonejs/core";
import cornerstoneDICOMImageLoader from "@cornerstonejs/dicom-image-loader";
import deepmerge from "deepmerge";

import { useMainStore } from "./main";
import { useUIStore } from "./ui";
import { useConfigStore } from "./config";
import { useEventsStore } from "./events";
import { setToolGroupIds } from "../composables/CornerstoneUtils";
import { useAlertStore }   from "./alert";
// Annotation tools own primary-click handle editing. On tool switch they're
// set Enabled (render but ignore input), not Passive — Passive keeps existing
// annotations clickable and silently hijacks the newly-activated tool.
const ANNOTATION_TOOLS = [
  "Length",
  "Probe",
  "RectangleROI",
  "EllipticalROI",
  "Bidirectional",
  "CobbAngle",
  "ArrowAnnotate",
  "Angle",
  "CircleROI",
  "PlanarFreehandROI",
  "SplineROI",
  "LivewireContour",
];

const INTERACTIVE_STACK_TOOLS = [
  "WindowLevel",
  "Pan",
  "Zoom",
  "StackScroll",
  "DragProbe",
  "Magnify",
  "WindowLevelRegion",
  "PlanarRotate",
  ...ANNOTATION_TOOLS,
];

// Short aliases accepted by ActivateTool. Unknown names pass through so
// callers can also pass canonical Cornerstone tool names directly.
const TOOL_ALIASES = {
  scroll: "StackScroll",
  wl: "WindowLevel",
  measure: "Length",
  length: "Length",
  pan: "Pan",
  zoom: "Zoom",
  angle: "Angle",
  rectROI: "RectangleROI",
  ellipseROI: "EllipticalROI",
  circleROI: "CircleROI",
  arrow: "ArrowAnnotate",
  cobb: "CobbAngle",
  cobbAngle: "CobbAngle",
  bidirectional: "Bidirectional",
  freehand: "PlanarFreehandROI",
  probe: "Probe",
  magnify: "Magnify",
  wlRegion: "WindowLevelRegion",
  planarRotate: "PlanarRotate",
  spline: "SplineROI",
  livewire: "LivewireContour",
  crosshairs: "Crosshairs",
  cursors: "ReferenceCursors",
  rotate: "TrackballRotate",
};

const normalize = (name) => TOOL_ALIASES[name] ?? name;

const dedupeBindings = (bindings) => {
  const seen = new Set();
  return bindings.filter((b) => {
    const key = `${b.mouseButton ?? "_"}:${b.numTouchPoints ?? "_"}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export const useToolsStore = defineStore("tools", {
  state: () => ({
    settingsOpened: false,
    VOIpresets: {
      CTAbdomen: { WL: 60, WW: 400 },
      CTAngio: { WL: 300, WW: 600 },
      CTBone: { WL: 300, WW: 1500 },
      CTBrain: { WL: 40, WW: 80 },
      CTChest: { WL: 40, WW: 400 },
      CTLungs: { WL: -400, WW: 1500 },
    },
    toolGroups: {
      stack: { id: "STACK_TOOLS" },
      volume: { id: "VOLUME_TOOLS" },
      vr: { id: "VR_TOOLS" },
    },
    // Mouse button → tool. Slot 0 also drives 1-finger touch.
    activeMouseTools: {
      0: "WindowLevel",
      1: "Pan",
      2: "Zoom",
    },
    annotationTools: ANNOTATION_TOOLS,
  }),

  actions: {
    // ── Viewport helpers ─────────────────────────────────────────────────────

    _getActiveViewports() {
      const mainStore = useMainStore();
      const visibleMulti = document.getElementsByClassName("multi-selected");

      if (
        mainStore.multiSelectedViewports.length > 0 &&
        visibleMulti.length > 0
      ) {
        return [...mainStore.multiSelectedViewports];
      }

      const elem =
        mainStore.selectedSeriesElement?.parentElement?.parentElement;
      if (!elem?.attributes?.["data-rendering-engine-uid"]) return [];

      const re = cornerstone3D.getRenderingEngine(
        elem.attributes["data-rendering-engine-uid"].value,
      );
      const viewport = re?.getViewport(
        elem.attributes["data-viewport-uid"].value,
      );
      return viewport ? [viewport] : [];
    },

    // ── Setup ─────────────────────────────────────────────────────────────────

    SetupToolGroups() {
      const {
        WindowLevelTool,
        PanTool,
        ZoomTool,
        StackScrollTool,
        TrackballRotateTool,
        LengthTool,
        ProbeTool,
        RectangleROITool,
        EllipticalROITool,
        BidirectionalTool,
        AngleTool,
        CircleROITool,
        DragProbeTool,
        MagnifyTool,
        ScaleOverlayTool,
        ArrowAnnotateTool,
        CrosshairsTool,
        ReferenceLinesTool,
        ReferenceCursors,
        CobbAngleTool,
        WindowLevelRegionTool,
        PlanarFreehandROITool,
        PlanarRotateTool,
        SplineROITool,
        LivewireContourTool,
        VolumeCroppingTool,
        ToolGroupManager,
        synchronizers,
      } = cornerstoneTools;

      annotation.config.style.setDefaultToolStyles(
        deepmerge(annotation.config.style.getDefaultToolStyles(), {
          ReferenceLines: { colorHighlighted: "rgb(0, 220, 0)" },
          global: {
            textBoxFontSize: "16px",
            textBoxBackground: "rgba(0, 0, 0, 0.1)",
            color: "#00BABA",
            textBoxColor: "#00BABA",
            colorHighlighted: "rgb(250, 0, 0)",
            textBoxColorHighlighted: "rgb(250, 0, 0)",
            colorSelected: "rgb(225, 0, 0)",
            textBoxColorSelected: "rgb(225, 0, 0)",
          },
        }),
      );

      const allTools = [
        WindowLevelTool,
        PanTool,
        ZoomTool,
        StackScrollTool,
        LengthTool,
        ProbeTool,
        RectangleROITool,
        EllipticalROITool,
        BidirectionalTool,
        CobbAngleTool,
        ArrowAnnotateTool,
        ReferenceLinesTool,
        AngleTool,
        CircleROITool,
        DragProbeTool,
        MagnifyTool,
        ScaleOverlayTool,
        WindowLevelRegionTool,
        PlanarFreehandROITool,
        PlanarRotateTool,
        SplineROITool,
        LivewireContourTool,
        CrosshairsTool,
        ReferenceCursors,
        TrackballRotateTool,
        VolumeCroppingTool,
      ];
      allTools.forEach((t) => cornerstoneTools.addTool(t));

      // Stack tool group
      const stackGroup = ToolGroupManager.createToolGroup(
        this.toolGroups.stack.id,
      );
      allTools.forEach((t) => {
        if (
          [
            CrosshairsTool,
            ReferenceCursors,
            TrackballRotateTool,
            VolumeCroppingTool,
          ].includes(t)
        )
          return;
        stackGroup.addTool(t.toolName);
      });
      stackGroup.setToolActive(StackScrollTool.toolName, {
        bindings: [{ mouseButton: MouseBindings.Wheel }],
      });
      stackGroup.setToolConfiguration(
        MagnifyTool.toolName,
        { magnifySize: 7, magnifyWidth: 220, magnifyHeight: 220 },
        true,
      );

      // MPR (volume) tool group
      const volumeGroup = ToolGroupManager.createToolGroup(
        this.toolGroups.volume.id,
      );
      const mprColors = {
        CT_AXIAL: "rgb(155 71 73)",
        CT_SAGITTAL: "rgb(193 190 100)",
        CT_CORONAL: "rgb(0 130 50)",
      };
      const mprControllable = Object.keys(mprColors);
      volumeGroup.addTool(CrosshairsTool.toolName, {
        getReferenceLineColor: (id) => mprColors[id],
        getReferenceLineControllable: (id) => mprControllable.includes(id),
        getReferenceLineDraggableRotatable: (id) =>
          mprControllable.includes(id),
        getReferenceLineSlabThicknessControlsOn: (id) =>
          mprControllable.includes(id),
      });
      volumeGroup.addTool(ReferenceCursors.toolName);
      volumeGroup.addTool(WindowLevelTool.toolName);
      volumeGroup.addTool(StackScrollTool.toolName);
      volumeGroup.addTool(PanTool.toolName);
      volumeGroup.addTool(ZoomTool.toolName);

      // VR tool group
      const vrGroup = ToolGroupManager.createToolGroup(this.toolGroups.vr.id);
      vrGroup.addTool(WindowLevelTool.toolName);
      vrGroup.addTool(TrackballRotateTool.toolName);
      vrGroup.addTool(PanTool.toolName);
      vrGroup.addTool(ZoomTool.toolName);
      vrGroup.addTool(VolumeCroppingTool.toolName);

      synchronizers.createStackImageSynchronizer("STACK_SYNCHRONIZER_ID");
      synchronizers.createVOISynchronizer("VOI_SYNCHRONIZER_ID");

      setToolGroupIds(this.toolGroups);
      this.ApplyStackInputBindings();
    },

    // ── Stack bindings ────────────────────────────────────────────────────────

    resetStackInteractiveTools(toolGroup) {
      INTERACTIVE_STACK_TOOLS.forEach((name) => {
        if (!toolGroup.toolOptions[name]) return;
        toolGroup.setToolDisabled(name);
        if (ANNOTATION_TOOLS.includes(name)) toolGroup.setToolEnabled(name);
      });
    },

    /**
     * Primary = left-click + 1-finger, auxiliary = middle, secondary = right.
     * Wheel always scrolls. Pinch always zooms. 3-finger always pans.
     */
    _buildStackBindings() {
      const bindings = {};
      const add = (tool, binding) => {
        if (!tool || tool === "none") return;
        (bindings[tool] = bindings[tool] || []).push(binding);
      };

      add(this.activeMouseTools[0], { mouseButton: MouseBindings.Primary });
      add(this.activeMouseTools[0], { numTouchPoints: 1 });
      add(this.activeMouseTools[1], { mouseButton: MouseBindings.Auxiliary });
      add(this.activeMouseTools[2], { mouseButton: MouseBindings.Secondary });
      add("Zoom", { numTouchPoints: 2 });
      add("Pan", { numTouchPoints: 3 });
      add("StackScroll", { mouseButton: MouseBindings.Wheel });

      return bindings;
    },

    ApplyStackInputBindings() {
      const toolGroup = cornerstoneTools.ToolGroupManager.getToolGroup(
        this.toolGroups.stack.id,
      );
      if (!toolGroup) return;

      this.resetStackInteractiveTools(toolGroup);

      Object.entries(this._buildStackBindings()).forEach(([tool, list]) => {
        if (list.length)
          toolGroup.setToolActive(tool, { bindings: dedupeBindings(list) });
      });
    },

    // ── Tool activation ───────────────────────────────────────────────────────

    /**
     * Single entry point for tool activation. Desktop toolbar, mobile toolbar,
     * context menu, and keyboard shortcuts all go through here.
     *
     * mouseButton = 0 (primary) also drives 1-finger touch — this is the only
     * activation model the app needs.
     */
    ActivateTool(toolName, mouseButton = 0) {
      if (toolName.includes("3D")) {
        this.Activate3DTool(toolName);
        return;
      }

      const tool = normalize(toolName);
      // A tool can only be bound to one slot at a time.
      for (const key of Object.keys(this.activeMouseTools)) {
        if (this.activeMouseTools[key] === tool)
          this.activeMouseTools[key] = "none";
      }
      this.activeMouseTools[mouseButton] = tool;
      this.ApplyStackInputBindings();
    },

    SetPrimaryTool(toolName) {
      this.ActivateTool(toolName, 0);
    },

    Activate3DTool(toolName) {
      const mainStore = useMainStore();
      if (!mainStore.mprMode) return;

      const toolGroup = cornerstoneTools.ToolGroupManager.getToolGroup(
        this.toolGroups.volume.id,
      );
      const base = toolName.split("-")[0];

      if (toolGroup.toolOptions[base]?.mode === "Active") {
        toolGroup.setToolDisabled(base);
      } else {
        ["Crosshairs", "WindowLevel", "ReferenceCursors"].forEach((t) =>
          toolGroup.setToolDisabled(t),
        );
        toolGroup.setToolActive(base, {
          bindings: [{ mouseButton: MouseBindings.Primary }],
        });
      }
      this.ApplyMprInputBindings();
    },

    // ── MPR bindings ──────────────────────────────────────────────────────────

    /**
     * Primary / 1-finger → Crosshairs.
     * Wheel / 2-finger   → scroll slices (2-finger fills in for wheel on touch).
     * Secondary          → Zoom.
     * Auxiliary / 3-finger → Pan.
     */
    ApplyMprInputBindings() {
      const { CrosshairsTool } = cornerstoneTools;
      const toolGroup = cornerstoneTools.ToolGroupManager.getToolGroup(
        this.toolGroups.volume.id,
      );
      if (!toolGroup) return;

      if (toolGroup.toolOptions[CrosshairsTool.toolName]?.mode !== "Active") {
        toolGroup.setToolActive(CrosshairsTool.toolName, {
          bindings: [
            { mouseButton: MouseBindings.Primary },
            { numTouchPoints: 1 },
          ],
        });
      }
      toolGroup.setToolActive("StackScroll", {
        bindings: [{ mouseButton: MouseBindings.Wheel }, { numTouchPoints: 2 }],
      });
      toolGroup.setToolActive("Pan", {
        bindings: [
          { mouseButton: MouseBindings.Auxiliary },
          { numTouchPoints: 3 },
        ],
      });
      toolGroup.setToolActive("Zoom", {
        bindings: [{ mouseButton: MouseBindings.Secondary }],
      });
    },

    // ── VR bindings ───────────────────────────────────────────────────────────

    /**
     * Primary is owned by TrackballRotate (the core 3D interaction users expect
     * when they drag on a volume). VolumeCropping is reachable via its own
     * handles, which are always visible — it doesn't need a shared primary
     * binding.
     *
     * Secondary / pinch   → Zoom.
     * Auxiliary / 3-finger → Pan.
     * Primary / 1-finger  → TrackballRotate.
     */
    ApplyVrInputBindings() {
      const toolGroup = cornerstoneTools.ToolGroupManager.getToolGroup(
        this.toolGroups.vr.id,
      );
      if (!toolGroup) return;

      toolGroup.setToolActive("TrackballRotate", {
        bindings: [
          { mouseButton: MouseBindings.Primary },
          { numTouchPoints: 1 },
        ],
      });
      toolGroup.setToolActive("Zoom", {
        bindings: [
          { mouseButton: MouseBindings.Secondary },
          { numTouchPoints: 2 },
        ],
      });
      toolGroup.setToolActive("Pan", {
        bindings: [
          { mouseButton: MouseBindings.Auxiliary },
          { numTouchPoints: 3 },
        ],
      });
      // VolumeCropping is Passive — it renders its handles and responds when
      // the user drags one, without competing for primary drags on empty space.
      toolGroup.setToolPassive("VolumeCropping");
    },

    // ── Synchronisation ───────────────────────────────────────────────────────

    SyncStacks(
      element,
      renderingEngine,
      renderingEngineId,
      stackSynchronizerId,
    ) {
      const mainStore = useMainStore();
      const visibleMulti = document.getElementsByClassName("multi-selected");
      if (
        mainStore.multiSelectedViewports.length > 0 &&
        visibleMulti.length > 0
      )
        return;

      const currentViewportId = element.attributes["data-viewport-uid"].value;
      const synchronizer =
        cornerstoneTools.SynchronizerManager.getSynchronizer(
          stackSynchronizerId,
        );
      if (!synchronizer) return;

      synchronizer
        .getSourceViewports()
        .slice()
        .forEach(({ viewportId }) =>
          synchronizer.removeSource({ viewportId, renderingEngineId }),
        );
      synchronizer
        .getTargetViewports()
        .slice()
        .forEach(({ viewportId }) =>
          synchronizer.removeTarget({ viewportId, renderingEngineId }),
        );

      const currentVP = renderingEngine
        .getViewports()
        .find((x) => x.id === currentViewportId);
      const thisFOR = cornerstoneDICOMImageLoader.wadors.metaDataManager.get(
        currentVP?.imageIds[currentVP?.getTargetImageIdIndex()],
      )?.["00200052"]?.Value?.[0];

      document.querySelectorAll(".viewport-element").forEach((el) => {
        const vp = renderingEngine.getViewport(
          el.parentElement.attributes["data-viewport-uid"].value,
        );
        if (!vp) return;
        if (vp.id === currentViewportId) {
          synchronizer.addSource({
            viewportId: currentViewportId,
            renderingEngineId,
          });
          return;
        }
        const targetFOR =
          cornerstoneDICOMImageLoader.wadors.metaDataManager.get(
            vp.imageIds[vp.getTargetImageIdIndex()],
          )?.["00200052"]?.Value?.[0];
        if (targetFOR && thisFOR && targetFOR === thisFOR) {
          synchronizer.addTarget({ viewportId: vp.id, renderingEngineId });
        }
      });
    },

    // ── Overlays / annotations ───────────────────────────────────────────────

    /**
     * Toggle patient-info state. The DOM sync is a single `v-show`-like
     * update colocated with the flag; pulling it into per-component watchers
     * would scatter the same line across Stack/MPR/VR for zero benefit.
     */
    TogglePatientInfo() {
      const uiStore = useUIStore();
      uiStore.showPatientInfo = !uiStore.showPatientInfo;
      document.querySelectorAll(".patient-info").forEach((el) => {
        el.style.display = uiStore.showPatientInfo ? "block" : "none";
      });
    },

    AddOrientationMarkersToViewport(viewport) {
      const id = viewport.imageIds[viewport.getTargetImageIdIndex()];
      const { rowCosines, columnCosines } =
        metaData.get("imagePlaneModule", id) || {};
      if (!rowCosines || !columnCosines) return;

      const markers = this.GetOrientationMarkers(
        rowCosines,
        columnCosines,
        parseInt(viewport.getRotation()),
        viewport.flipVertical,
        viewport.flipHorizontal,
      );

      const container = viewport.element.parentElement.parentElement;
      const set = (selector, value) => {
        const p = container.querySelector(`${selector} .primary-marker`);
        const s = container.querySelector(`${selector} .secondary-marker`);
        if (p) p.innerHTML = value.substring(0, 1);
        if (s) s.innerHTML = value.substring(1, 2);
      };
      set(".left-middle", markers.left);
      set(".right-middle", markers.right);
      set(".top-middle", markers.top);
      set(".bottom-middle", markers.bottom);
    },

    ActivateRefLines(svgContainer = null) {
      const toolGroup = cornerstoneTools.ToolGroupManager.getToolGroup(
        this.toolGroups.stack.id,
      );
      toolGroup.setToolEnabled("ReferenceLines");
      useConfigStore().refLinesEnabled = true;
      svgContainer?.classList.add("ref-active");
    },

    DeactivateRefLines(svgContainer = null) {
      const toolGroup = cornerstoneTools.ToolGroupManager.getToolGroup(
        this.toolGroups.stack.id,
      );
      toolGroup.setToolDisabled("ReferenceLines");
      useConfigStore().refLinesEnabled = false;
      svgContainer?.classList.remove("ref-active");
    },

    ChangeRefLines() {
      const mainStore = useMainStore();
      if (!mainStore.selectedSeriesElement) return;

      let viewportId;
      try {
        viewportId =
          mainStore.selectedSeriesElement.parentElement.parentElement
            .attributes["data-viewport-uid"].value;
      } catch {
        return;
      }

      const toolGroup = cornerstoneTools.ToolGroupManager.getToolGroup(
        this.toolGroups.stack.id,
      );
      toolGroup.setToolConfiguration(
        "ReferenceLines",
        { sourceViewportId: viewportId },
        true,
      );
      toolGroup.setToolEnabled("ReferenceLines");
    },

    EraseOne() {
      const { selection, visibility } = annotation;
      const uids = selection.getAnnotationsSelected();
      if (uids?.length) visibility.setAnnotationVisibility(uids[0], false);
    },

    EraseAll() {
      const mainStore = useMainStore();
      if (!mainStore.selectedSeriesElement) return;

      const elem = mainStore.selectedSeriesElement.parentElement.parentElement;
      const renderEngineId =
        elem.attributes["data-rendering-engine-uid"]?.value;
      if (!renderEngineId) return;

      const renderingEngine = cornerstone3D.getRenderingEngine(renderEngineId);
      const manager = cornerstoneTools.annotation.state.getAnnotationManager();

      // Keep ScaleOverlay, drop everything else.
      const preserved = {};
      for (const key in manager.annotations) {
        if (manager.annotations[key].ScaleOverlay) {
          preserved[key] = {
            ScaleOverlay: manager.annotations[key].ScaleOverlay,
          };
        }
      }
      manager.annotations = preserved;
      renderingEngine.getViewports().forEach((vp) => vp.render());
    },

    // ── Viewport operations ───────────────────────────────────────────────────

    Reset() {
      const events = useEventsStore();
      for (const vp of this._getActiveViewports()) {
        events.clearViewportProperty(vp.id);
        vp.resetCamera();
        vp.resetProperties();
        vp.render();
      }
    },

    ResetWWWC() {
      for (const vp of this._getActiveViewports()) {
        vp.resetProperties();
        vp.render();
      }
    },

    Invert() {
      for (const vp of this._getActiveViewports()) {
        vp.setProperties({ invert: !vp.getProperties().invert });
        vp.render();
      }
    },

    RotateLeft() {
      this._rotate(-90);
    },
    RotateRight() {
      this._rotate(90);
    },
    _rotate(deg) {
      for (const vp of this._getActiveViewports()) {
        vp.setRotation(vp.getRotation() + deg);
        vp.render();
      }
    },

    FlipV() {
      this._flip("flipVertical");
    },
    FlipH() {
      this._flip("flipHorizontal");
    },
    _flip(axis) {
      for (const vp of this._getActiveViewports()) {
        vp.setCamera({ [axis]: !vp.getCamera()[axis] });
        vp.render();
      }
    },

    FirstIndex() {
      this._jumpTo(0);
    },
    LastIndex() {
      for (const vp of this._getActiveViewports())
        this._jumpViewport(vp, vp.imageIds.length - 1);
    },
    _jumpTo(idx) {
      for (const vp of this._getActiveViewports()) this._jumpViewport(vp, idx);
    },
    _jumpViewport(vp, idx) {
      vp.setImageIdIndex(idx);
      vp.currentImageIdIndex = idx;
      vp.targetImageIdIndex = idx;
    },

    ChangeToWindowingPreset(preset) {
      const { WL, WW } = this.VOIpresets[preset];
      const voiRange = csUtils.windowLevel.toLowHighRange(WW, WL);
      for (const vp of this._getActiveViewports()) {
        vp.setProperties({ voiRange });
        vp.element.setAttribute("WL", WL);
        vp.element.setAttribute("WW", WW);
        vp.render();
      }
    },

    GetOrientationMarkers(rowCosines, columnCosines, rotation, flipV, flipH) {
      const { getOrientationStringLPS, invertOrientationStringLPS } =
        utilities.orientation;
      const inv = invertOrientationStringLPS;

      const rowString = getOrientationStringLPS(rowCosines);
      const colString = getOrientationStringLPS(columnCosines);

      let m = {
        top: inv(colString),
        left: inv(rowString),
        right: rowString,
        bottom: colString,
      };

      if (flipV) {
        m.top = inv(m.top);
        m.bottom = inv(m.bottom);
      }
      if (flipH) {
        m.left = inv(m.left);
        m.right = inv(m.right);
      }

      if (rotation === 90 || rotation === -270)
        return {
          top: m.left,
          left: inv(m.top),
          right: inv(m.bottom),
          bottom: m.right,
        };
      if (rotation === -90 || rotation === 270)
        return {
          top: inv(m.left),
          left: m.top,
          right: m.bottom,
          bottom: m.left,
        };
      if (Math.abs(rotation) === 180)
        return {
          top: inv(m.top),
          left: inv(m.left),
          right: inv(m.right),
          bottom: inv(m.bottom),
        };
      return m;
    },

    ToggleScaleOverlay() {
      const toolGroup = cornerstoneTools.ToolGroupManager.getToolGroup(
        this.toolGroups.stack.id,
      );
      if (toolGroup.toolOptions.ScaleOverlay?.mode !== "Disabled") {
        toolGroup.setToolDisabled("ScaleOverlay");
      } else {
        toolGroup.setToolPassive("ScaleOverlay");
      }
      cornerstone3D.getRenderingEngines().forEach((re) => {
        re.getViewports().forEach((vp) => vp.resetCamera());
      });
    },

    // ── 3D modes (mutually exclusive) ────────────────────────────────────────

    EnterMPRMode() {
      const mainStore = useMainStore();
      if (!mainStore.selectedSeriesElement) {
        alert("Please select a series before entering MPR mode");
        return;
      }
      const elem = mainStore.selectedSeriesElement.parentElement.parentElement;
      if (!elem.attributes["data-rendering-engine-uid"]) {
        alert("Please select a series before entering MPR mode");
        return;
      }

      if (mainStore.vrMode) this.ExitVRMode();

      const re = cornerstone3D.getRenderingEngine(
        elem.attributes["data-rendering-engine-uid"].value,
      );
      const viewport = re.getViewport(
        elem.attributes["data-viewport-uid"].value,
      );
      const metadata = cornerstoneDICOMImageLoader.wadors.metaDataManager.get(
        viewport.imageIds[viewport.getTargetImageIdIndex()],
      );

      mainStore.mprStudyUID = metadata["0020000D"].Value[0];
      mainStore.mprSeriesUID = metadata["0020000E"].Value[0];
      mainStore.mprMode = true;
    },

    ExitMPRMode() {
      const mainStore = useMainStore();
      mainStore.mprMode = false;
      mainStore.mprStudyUID = "";
      mainStore.mprSeriesUID = "";
    },

    EnterVRMode() {
      const mainStore = useMainStore();
      if (mainStore.mprMode) this.ExitMPRMode();
      mainStore.vrMode = true;
    },

    ExitVRMode() {
      useMainStore().vrMode = false;
    },

    Exit3DMode() {
      const mainStore = useMainStore();
      if (mainStore.mprMode) this.ExitMPRMode();
      if (mainStore.vrMode) this.ExitVRMode();
    },

    // ── CINE ─────────────────────────────────────────────────────────────────

    /**
     * Play/stop a series. Pass the originating event for cine-control button
     * clicks (plays the clip owning that button); pass null to play whichever
     * series is currently selected.
     */
    PlaySeries(e = null) {
      this._cine(e, "play");
    },
    StopSeries(e = null) {
      this._cine(e, "stop");
    },

    _cine(e, action) {
      const mainStore = useMainStore();
      const alertStore = useAlertStore();
      const { cine } = cornerstoneTools.utilities;
      const fps = mainStore.seriesFPS;

      const run = (el) =>
        action === "play"
          ? cine.playClip(el, { framesPerSecond: fps })
          : cine.stopClip(el);

      if (e?.target?.closest(".cine-controls")) {
        run(e.target.closest(".canvas-container").children[0].children[0]);
        return;
      }

      if (!mainStore.selectedSeriesElement) {
        if (action === "play") {
          alertStore.showNeutralAlert({
            text: "Please select a series to play.",
          });
        }
        return;
      }

      const el = mainStore.selectedSeriesElement.parentElement.parentElement;
      el.setAttribute("cine-status", action);

      if (el.attributes["data-rendering-engine-uid"]) {
        run(el);
      } else if (action === "play") {
        alertStore.showNeutralAlert({
          text: "Please select a series to play.",
        });
      }
    },

    ChangeFPS() {
      const mainStore = useMainStore();
      if (!mainStore.selectedSeriesElement) return;

      const el = mainStore.selectedSeriesElement.parentElement.parentElement;
      if (!el.attributes["data-rendering-engine-uid"]) return;

      const { cine } = cornerstoneTools.utilities;
      cine.stopClip(el);
      if (el.attributes["cine-status"]?.value === "play") {
        cine.playClip(el, { framesPerSecond: mainStore.seriesFPS });
      }
    },

    // ── Utilities ─────────────────────────────────────────────────────────────

    getToolGroupMouseBindingFromEventWhichValue(mouseButton) {
      const { MouseBindings: MB } = csToolsEnums;
      return (
        { 0: MB.Primary, 1: MB.Auxiliary, 2: MB.Secondary }[mouseButton] ??
        mouseButton
      );
    },
  },
});
